import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  Check,
  ShieldCheck,
  ExternalLink,
  ArrowLeft,
  Wallet,
  User,
  Mail,
  Phone,
  CreditCard,
  FileText,
  Hash,
  Calendar,
} from "lucide-react";

function DetailRow({ icon: Icon, label, value, copiable, mono }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!value) return null;

  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        <span className="text-sm text-slate-500">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>
          {value}
        </span>
        {copiable && (
          <button
            onClick={handleCopy}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function StatusIndicator({ status }) {
  const config = {
    success: {
      icon: CheckCircle2,
      label: "Payment Successful",
      bg: "bg-emerald-100",
      text: "text-emerald-600",
      badgeBg: "bg-emerald-50 border-emerald-200",
      badgeText: "text-emerald-700",
    },
    declined: {
      icon: XCircle,
      label: "Payment Declined",
      bg: "bg-red-100",
      text: "text-red-600",
      badgeBg: "bg-red-50 border-red-200",
      badgeText: "text-red-700",
    },
    failed: {
      icon: XCircle,
      label: "Payment Failed",
      bg: "bg-red-100",
      text: "text-red-600",
      badgeBg: "bg-red-50 border-red-200",
      badgeText: "text-red-700",
    },
  };

  const c = config[status] || {
    icon: Clock,
    label: "Payment Pending",
    bg: "bg-amber-100",
    text: "text-amber-600",
    badgeBg: "bg-amber-50 border-amber-200",
    badgeText: "text-amber-700",
  };

  const Icon = c.icon;

  return (
    <>
      <div className={`w-20 h-20 ${c.bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
        <Icon className={`h-10 w-10 ${c.text}`} />
      </div>
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${c.badgeBg} mb-2`}>
        <Icon className={`h-4 w-4 ${c.badgeText}`} />
        <span className={`text-sm font-medium ${c.badgeText}`}>{c.label}</span>
      </div>
    </>
  );
}

export default function DepositReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const paymentId = params.get("payment_id") || params.get("pp_TxnRefNo") || "";
  const amount = params.get("amount") || params.get("pp_Amount") || "";
  const message = params.get("message") || params.get("pp_ResponseMessage") || "";
  const txnRef = params.get("merchant_transaction_id") || params.get("pp_MerchantID") || "";
  const currency = params.get("currency") || "PKR";
  const method = params.get("payby") || params.get("method") || "";
  const rawStatus = params.get("status") || params.get("pp_ResponseCode") || "pending";
  const hashVerified = params.get("hash_verified");
  const checkoutUrl = params.get("checkout_url") || "";
  const customerName = params.get("customer_name") || "";
  const customerEmail = params.get("customer_email") || "";
  const customerPhone = params.get("customer_phone") || "";
  const timestamp = new Date().toLocaleString();

  const status =
    rawStatus === "000" || rawStatus === "0" || rawStatus === "success"
      ? "success"
      : rawStatus === "declined" || rawStatus === "failed" || rawStatus === "157"
        ? "declined"
        : "pending";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="text-center pt-8 pb-4 px-8">
            <StatusIndicator status={status} />
            <h1 className="text-2xl font-bold text-slate-900 mt-2">
              Deposit Receipt
            </h1>
            {message && (
              <p className="text-slate-500 text-sm mt-1">{message}</p>
            )}
          </div>

          {/* Amount Card */}
          {amount && (
            <div className="px-8 pb-4">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-5 text-white text-center">
                <p className="text-sm opacity-80 mb-1">Amount</p>
                <p className="text-3xl font-bold">
                  {currency === "PKR" ? "Rs" : currency}{" "}
                  {parseFloat(amount).toLocaleString()}
                </p>
                {method && (
                  <p className="text-sm opacity-80 mt-1">via {method}</p>
                )}
              </div>
            </div>
          )}

          {/* Transaction Details */}
          <div className="px-8 pb-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Transaction Details
              </h3>
              <DetailRow icon={Hash} label="Payment ID" value={paymentId} copiable mono />
              <DetailRow icon={FileText} label="Transaction Ref" value={txnRef} copiable mono />
              <DetailRow icon={CreditCard} label="Payment Method" value={method} />
              <DetailRow icon={Wallet} label="Currency" value={currency} />
              <DetailRow icon={Calendar} label="Date & Time" value={timestamp} />
            </div>
          </div>

          {/* Customer Details */}
          {(customerName || customerEmail || customerPhone) && (
            <div className="px-8 pb-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Customer Details
                </h3>
                <DetailRow icon={User} label="Name" value={customerName} />
                <DetailRow icon={Mail} label="Email" value={customerEmail} />
                <DetailRow icon={Phone} label="Phone" value={customerPhone} />
              </div>
            </div>
          )}

          {/* Checkout URL */}
          {checkoutUrl && (
            <div className="px-8 pb-4">
              <div className="bg-blue-50 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">
                  Checkout Link
                </h3>
                <p className="text-xs text-slate-500 mb-2">
                  Share this link with the customer to complete payment:
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white border border-blue-200 rounded-lg px-3 py-2 text-xs text-slate-700 truncate font-mono">
                    {checkoutUrl}
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(checkoutUrl)}
                    className="shrink-0 p-2 rounded-lg bg-white border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => window.open(checkoutUrl, "_blank")}
                    className="shrink-0 p-2 rounded-lg bg-white border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Hash Verification & Security */}
          <div className="px-8 pb-6">
            {hashVerified === "true" && (
              <div className="flex items-center justify-center gap-2 text-sm text-emerald-600 mb-3">
                <ShieldCheck className="h-4 w-4" />
                <span>Hash verified - Transaction is authentic</span>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
              <ShieldCheck className="h-4 w-4" />
              <span>Secured by Altafinex</span>
            </div>
          </div>

          {/* Actions */}
          <div className="px-8 pb-8">
            <button
              onClick={() => navigate("/merchant/deposits")}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Deposits
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
